if (!customElements.get('cart-upsells')) {
  class CartUpsells extends HTMLElement {
    constructor() {
      super();
      this.submitButtons = this.querySelectorAll('[data-cart-upsells-product-submit]');
    }

    connectedCallback() {
      this.submitButtons.forEach((submitButton) => submitButton.addEventListener('click', () => this.addToCart(submitButton)));
    }

    addToCart(button) {
      const variantId = button.getAttribute('data-variant-id');

      const cartSubmission = {
        items: [
          {
            id: variantId,
            quantity: 1
          }
        ]
      };

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'xmlhttprequest'
        },
        body: JSON.stringify(cartSubmission)
      }).then((response) => {
        return response.json()
      }).then((result) => {
        if (result.status) {
          throw new Error(result.description)
        }

        document.dispatchEvent(new Event('cart:refresh'));

        return null
      }).catch((error) => {
        alert(error)
      });
    }
  }

  customElements.define('cart-upsells', CartUpsells);
}

class CartThresholdBar extends HTMLElement {
  constructor() {
    super();
    this.freeGifts = this.getAttribute('data-free-gifts') ? this.getAttribute('data-free-gifts').substring(0, this.getAttribute('data-free-gifts').length - 1).split(',') : null;
    this.button = this.querySelector('[data-testing]');
  }

  async connectedCallback() {
    if (this.freeGifts?.length) {
      for (const freeGift of this.freeGifts) {
        const cartData = await getCart();
        const variantId = freeGift.split(':')[0];
        const threshold = freeGift.split(':')[1];
        const freeGiftProduct = cartData.items.find((item) => item.properties._free_gift && item.id == variantId);
        const freeGiftQualifies = (cartData.total_price / 100) >= threshold;

        if (freeGiftQualifies && !freeGiftProduct) {
          this.addFreeGift(variantId, threshold);
        } else if(!freeGiftQualifies && freeGiftProduct) {
          this.removeFreeGift(freeGiftProduct.key);
        }
      }

      this.validateFreeGiftsInCart();
    }
  }

  async validateFreeGiftsInCart() {
    const cartData = await getCart();
    const freeGiftsInCart = cartData.items.filter((item) => item.properties._free_gift);

    freeGiftsInCart.forEach((cartGift) => {
      const isValid = this.freeGifts.find(freeGift => freeGift.includes(cartGift.id));
      if (isValid) { return; }
      this.removeFreeGift(cartGift.key);
    });
  }

  addFreeGift(variantId, threshold) {
    const postBody = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            _free_gift_threshold: threshold,
            _free_gift: 'true'
          }
        }
      ]
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(postBody)
    }).then((response) => {
      return response.json()
    }).then(async (response) => {
      document.dispatchEvent(new Event('cart:refresh'));
    }).catch((error) => {
      console.error('Error adding free gift to cart:', error);
    })
  }

  removeFreeGift(lineItemKey) {
    const updates = {
      [lineItemKey]: 0
    };

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates })
    })
    .then(response => {
      return response.json();
    })
    .then(async (response) => {
      document.dispatchEvent(new Event('cart:refresh'));
    })
    .catch((error) => {
      console.error('Error:', error);
    });
  }
}

customElements.define('cart-threshold-bar', CartThresholdBar);

const StoryCarousel = class extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[data-story-carousel]');
    this.slides = this.querySelectorAll('[data-story-slide]');
    this.allProgressIndicators = this.querySelectorAll('[data-social-overlay-progress]');
    this.videos = this.querySelectorAll('[data-story-video]');
    this.videoUnmuteButtons = this.querySelectorAll('[data-story-video-unmute]');
    this.videoMuteButtons = this.querySelectorAll('[data-story-video-mute]');
    this.allStories = this.querySelectorAll('[data-story-page]');
    this.prevButtons = this.querySelectorAll('[data-story-back]');
    this.nextButtons = this.querySelectorAll('[data-story-next]');
    this.carouselPrevious = this.querySelector('[data-carousel-previous]');
    this.carouselNext = this.querySelector('[data-carousel-next]');
    this.started = false;
    this.paused = false;
  }

  connectedCallback () {
    if (!this.slides.length) { return; }

    const options = {
      cellAlign: 'center',
      adaptiveHeight: false,
      wrapAround: false,
      imagesLoaded: true,
      pageDots: false,
      contain: true,
      prevNextButtons: true,
      arrowShape: 'M0 50.0026L40.0875 11L47.9095 18.6092L21.1755 44.6215L100 44.6163V55.3785L21.1755 55.3838L47.9095 81.3908L40.0875 89L0 50.0026Z',
      on: {
        ready: () => {
          this.checkIfOnScreen();
        },
      },
    };

    this.flickity = new Flickity(this.slider, options);

    this.flickity.on('change', (index) => {
      this.stopVideos();
      this.resetSlides();
      this.beginSlideTimer(index);
    });

    this.slides.forEach(slide => {
      slide.addEventListener('click', (event) => {
        if (slide.classList.contains('is-selected') || event.target.localName === 'button') { return; }
        this.flickity.selectCell([...this.slides].findIndex(el => el === slide));
      });
    });

    this.slider.addEventListener('mouseenter', () => {
      this.paused = true;
    });

    this.slider.addEventListener('mouseleave', () => {
      this.paused = false;
    });

    this.prevButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.goPrevious(button);
      });
    });

    this.nextButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.goNext(button);
      });
    });

    this.allProgressIndicators.forEach(indicator => {
      indicator.addEventListener('click', () => {
        const slide = indicator.closest('[data-story-slide]');

        if (!slide.classList.contains('is-selected')) { return; }

        const indicatorIndex = indicator.getAttribute('data-social-overlay-progress');
        const activeIndicatorIndex = Number(slide.dataset.index);

        if (indicatorIndex > activeIndicatorIndex) {
          clearInterval(this.interval);
          this.beginSlideTimer([...this.slides].findIndex(el => el === slide), indicatorIndex);
        } else if (indicatorIndex < activeIndicatorIndex) {
          this.resetSlides();
          this.beginSlideTimer([...this.slides].findIndex(el => el === slide), indicatorIndex);
        }

        setTimeout(() => indicator.classList.add('active'), 10);
      });
    });

    this.videoUnmuteButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.slides.forEach((slide) => {
          const video = slide.querySelector('[data-story-video]');
          slide.classList.add('story-slide--muted');
          if (video) {
            video.muted = false;
          }
        });
      });
    });

    this.videoMuteButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.slides.forEach((slide) => {
          const video = slide.querySelector('[data-story-video]');
          slide.classList.remove('story-slide--muted');
          if (video) {
            video.muted = true;
          }
        });
      });
    });
  }

  checkIfOnScreen() {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (!this.started) {
            this.started = true;
            this.beginSlideTimer(0);
          } else if (this.paused) {
            const storySlide = this.querySelector('[data-story-slide].is-selected');
            const video = storySlide.querySelector('[data-story-video]');

            if (video) {
              storySlide.classList.remove('story-slide--paused');
              video.play();
              this.paused = false;
            }
          }
        } else {
          if (this.started && !this.paused) {
            const storySlide = this.querySelector('[data-story-slide].is-selected');
            const video = storySlide.querySelector('[data-story-video]');

            if (video) {
              storySlide.classList.add('story-slide--paused');
              video.pause();
              this.paused = true;
            }
          }
        }
      });
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    });

    observer.observe(this);
  }

  stopVideos() {
    this.videos.forEach((video) => {
      video.pause();
    });
  }

  resetSlides () {
    clearInterval(this.interval);

    this.slides.forEach(slide => {
      slide.setAttribute('data-index', 0);
    });

    this.allStories.forEach(story => {
      story.classList.remove('active');
    });

    this.allProgressIndicators.forEach(indicator => {
      indicator.classList.remove('active');
      indicator.classList.remove('skipped');
    });
  }

  beginSlideTimer (slideIndex, startingIndex = 0) {
    const slide = this.slides[slideIndex];
    const storyPages = slide.querySelectorAll('[data-story-page]');
    const indicators = slide.querySelectorAll('[data-social-overlay-progress]');
    let storyIndex = startingIndex;
    const storyVideo = this.slides[slideIndex].querySelector('[data-story-video]');
    const storyVideoLength = storyVideo ? parseInt(storyVideo.getAttribute('data-story-video-length')) : null;
    const slideLength = storyVideoLength ? (storyVideoLength * 1000) : 5000;

    if (storyVideo) {
      slide.classList.remove('story-slide--paused');
      storyVideo.load();
      slide.classList.add('story-slide--video-loading');
      storyVideo.addEventListener('loadeddata', function () {
        slide.classList.remove('story-slide--video-loading');
      }, false);
      storyVideo.play();
    }

    if (storyPages[storyIndex]) {
      storyPages[storyIndex].classList.add('active');
    }

    indicators.forEach((indicator, index) => {
      indicator.classList.remove('active');
      setTimeout(() => {
        indicator.classList.toggle('skipped', index < startingIndex);
        indicator.classList.toggle('active', index === startingIndex);
      }, 10);
    });

    slide.setAttribute('data-index', storyIndex);

    this.interval = setInterval(() => {
      storyIndex++;
      slide.setAttribute('data-index', storyIndex);

      if (storyPages.length <= storyIndex) {
        if (this.paused) { return; }

        this.stopVideos();
        clearInterval(this.interval);
        this.flickity.next();
        return;
      }

      indicators[storyIndex].classList.add('active');

      storyPages.forEach((storyPage, index) => {
        storyPage.classList.toggle('active', index === storyIndex);
      });

      indicators[storyIndex].classList.add('active');
    }, slideLength);
  }

  goPrevious (button) {
    const slide = button.closest('[data-story-slide]');

    if (!slide.classList.contains('is-selected')) { return; }

    const activeIndicatorIndex = Number(slide.dataset.index);

    if (activeIndicatorIndex === 0) {
      this.flickity.previous();
      return;
    }

    this.resetSlides();
    this.beginSlideTimer([...this.slides].findIndex(el => el === slide), (activeIndicatorIndex - 1));
  }

  goNext (button) {
    const slide = button.closest('[data-story-slide]');

    if (!slide.classList.contains('is-selected')) { return; }

    const activeIndicatorIndex = Number(slide.dataset.index);
    const storyPages = slide.querySelectorAll('[data-story-page]');

    if (activeIndicatorIndex === (storyPages.length - 1)) {
      this.flickity.next();
      return;
    }

    clearInterval(this.interval);

    this.beginSlideTimer([...this.slides].findIndex(el => el === slide), (activeIndicatorIndex + 1));
  }
};

if (!window.customElements.get("story-carousel")) {
  window.customElements.define("story-carousel", StoryCarousel);
}

const LineItemSellingPlanSelector = class extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.key = this.dataset.key;
    this.value = this.select.value;
    this.enableCheckbox = this.querySelector('[data-enable-selling-plan]');
    this.selectWrapper = this.querySelector('[data-selling-plan-select]');
    this.checkboxWrapper = this.querySelector('[data-enable-selling-plan-wrap]');
    this.noPlanOption = this.querySelector('[data-none-value]');
    this.planOptions = this.querySelectorAll('[data-real-value]');
  }

  connectedCallback() {
    this.getCart();
  }

  async getCart() {
    const cart = await getCart();
    this.cartItems = cart.items;
    this.init();
  }

  init() {
    this.cartItem = this.cartItems.find(item => item.key === this.key);
    this.selectWrapper.classList.toggle('hidden', !this.enableCheckbox.checked);
    this.checkboxWrapper.classList.toggle('hidden', this.enableCheckbox.checked);
    this.enableCheckbox.addEventListener('change', this.toggleSellingPlan.bind(this));
    this.select.addEventListener('change', this.changeSellingPlan.bind(this));
  }

  toggleSellingPlan () {
    this.selectWrapper.classList.toggle('hidden', !this.enableCheckbox.checked);
    this.checkboxWrapper.classList.toggle('hidden', this.enableCheckbox.checked);
    this.select.value = this.enableCheckbox.checked ? this.planOptions[0].value : this.noPlanOption.value;
    const changeEvent = new Event('change');
    this.select.dispatchEvent(changeEvent);
  }

  async changeSellingPlan() {
    if (this.select.value === this.value) { return; }

    const changeData = {
      id: this.cartItem.key,
      quantity: this.cartItem.quantity,
      selling_plan: (this.select.value === '0' ? null : Number(this.select.value))
    };

    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        "Content-Type": 'application/json'
      },
      body: JSON.stringify(changeData)
    });

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));

    if (window.themeVariables.settings.pageType !== 'cart') { return; }

    window.location.reload();
  }
};

if (!window.customElements.get('line-item-selling-plan-selector')) {
  window.customElements.define('line-item-selling-plan-selector', LineItemSellingPlanSelector);
}

if (!customElements.get('product-subscriptions')) {
  class ProductSubscriptions extends HTMLElement {
    constructor() {
      super()

      this.parentProduct = this.closest('product-rerender');
      this.form = this.parentProduct.querySelector('form');
      this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
      this.variantSellingPlanGroupings = this.querySelectorAll('[data-selling-plan-variant-grouping]');
      this.variantIdInput = this.form.querySelector('input[name="id"]');
      this.sellingPlanOptionWrappers = this.querySelectorAll('[data-selling-plan-wrapper]');
      this.oneTimeSellingPlanOption = this.querySelector('[data-selling-plan][data-radio-type="one_time_purchase"]');
      this.sellingPlanOptions = this.querySelectorAll('[data-selling-plan]');
      this.sellingPlanOptions.length;
      this.initialSellingPlanLabel = this.querySelector('[data-initial-selling-plan-label]');
      this.atcButton = this.form.querySelector('[data-product-subscriptions-submit]');
    }

    connectedCallback () {
      this.sellingPlanOptions.forEach(sellingPlanOption => {
        sellingPlanOption.addEventListener('change', () => {
          this.toggleSellingPlans(sellingPlanOption);
        })
      })

      if (this.initialSellingPlanLabel) {
        this.initialSellingPlanLabel.click();
      } else {
        const firstSellingPlanLabel = this.sellingPlanOptionWrappers[0].querySelector('label');
        firstSellingPlanLabel.click();
      }

      this.atcButton.addEventListener('click', () => this.addToCart());

      window.addEventListener('load', () => this.removeRechargeWidget());
    }

    removeRechargeWidget() {
      let retryCount = 0;
      const maxRetries = 20;

      let interval = setInterval(() => {
        const rechargeWidget = document.querySelector('.rc-container-wrapper');

        if(rechargeWidget) {
          rechargeWidget.remove();
          clearInterval(interval);
        }

        retryCount++;
        if (retryCount >= maxRetries) {
          clearInterval(interval);
        }
      }, 2000)
    }

    toggleSellingPlans(sellingPlanOption) {
      const galleryThumbnails = document.querySelectorAll('[data-product-gallery-thumbnail]');
      const sellingPlanVariantImage = sellingPlanOption.getAttribute('data-variant-image')?.split('?')[0];
      this.sellingPlanInput.value = sellingPlanOption.value;
      this.sellingPlanInput.name = sellingPlanOption.value ? 'selling_plan' : '';
      this.variantIdInput.value = sellingPlanOption.dataset.variantId;

      this.setBuyButtonPrice(sellingPlanOption);
      this.setSpecialMessage(sellingPlanOption);

      this.sellingPlanOptionWrappers.forEach(sellingPlanOptionWrapper => {
        sellingPlanOptionWrapper.classList.toggle('active', sellingPlanOptionWrapper.contains(sellingPlanOption));
      });

      galleryThumbnails.forEach((thumbnail) => {
        const thumbnailSrc = thumbnail.querySelector('img')?.getAttribute('src')?.split('?')[0];

        if (!thumbnailSrc || thumbnailSrc != sellingPlanVariantImage) { return; }

        thumbnail.click();
      });
    }

    setSpecialMessage(sellingPlanOption) {
      const specialMessage = sellingPlanOption.getAttribute('data-special-message');
      const parentWrapper = sellingPlanOption.closest('product-rerender');
      const specialMessageElement = parentWrapper.querySelector('[data-product-special-message]');

      if (specialMessageElement) {
        specialMessageElement.innerText = specialMessage;
        specialMessageElement.classList.toggle('hidden', !specialMessage);
      }
    }

    setBuyButtonPrice(sellingPlanOption) {
      const variantPrice = sellingPlanOption.getAttribute('data-variant-price');
      const variantComparePrice = sellingPlanOption.getAttribute('data-variant-compare-price');
      const parentWrapper = sellingPlanOption.closest('product-rerender');
      const buyButtonPrice = parentWrapper?.querySelector('[data-product-atc-price]');
      const buyButtonComparePrice = parentWrapper?.querySelector('[data-product-atc-compare-price]');

      if (buyButtonPrice) {
        buyButtonPrice.innerHTML = variantPrice;
      }

      if (buyButtonComparePrice) {
        buyButtonComparePrice.innerHTML = variantComparePrice;
      }
    }

    async addToCart() {
      const variantId = this.form.querySelector('input[name="id"]')?.value;
      const sellingPlanId = this.form.querySelector('input[name="selling_plan"]')?.value;
      const activeSellingPlan = Array.from(this.sellingPlanOptionWrappers).find((sellingPlan) => sellingPlan.classList.contains('active'));
      const sellingPlanLabel = activeSellingPlan?.querySelector('[data-selling-plan-label]');
      const freeGifts = activeSellingPlan?.querySelectorAll('[data-product-subscription-free-gift]');

      if (!variantId) { return; }

      const formData = {
        items: []
      };

      if (freeGifts.length) {
        const cart = await getCart();
        const freeGiftsInCart = cart.items.filter((item) => item.properties['_bundle_child']);
        const bundleId = Date.now();
        const bundleChildren = [];

        Array.from(freeGifts).map((freeGift) => {
          const freeGiftVariantId = freeGift.getAttribute('data-product-subscription-free-gift');
          const giftIsAlreadyInCart = freeGiftsInCart.find((freeGift) => freeGift.variant_id === parseInt(freeGiftVariantId));

          bundleChildren.push(freeGiftVariantId);

          if (giftIsAlreadyInCart) { return; }

          formData.items.push({
            id: freeGiftVariantId,
            quantity: 1,
            properties: {
              _bundle_id: bundleId,
              _bundle_child: true,
            }
          });

          return null;
        });

        formData.items.push({
          id: variantId,
          quantity: 1,
          selling_plan: sellingPlanId,
          properties: {
            _bundle_id: bundleId,
            _bundle_parent: true,
            _program_name: sellingPlanLabel?.innerText,
            _bundle_children: bundleChildren
          }
        });
      } else {
        formData.items.push({
          id: variantId,
          quantity: 1,
          selling_plan: sellingPlanId,
        });
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error(`Response status: ${response.status}`);
        }

        if (this.closest('[data-send-user-to-checkout]')) {
          window.location.href = '/checkout';
        } else {
          document.dispatchEvent(new Event('cart:refresh'));
          const cartDrawerToggle = document.querySelector('a[aria-controls="cart-drawer"]');

          if (cartDrawerToggle) {
            cartDrawerToggle.click();
          }
        }
      } catch (error) {
        console.error(error.message);
      }
    }
  }

  window.customElements.define('product-subscriptions', ProductSubscriptions);
}

if (!customElements.get('cart-bundle-validation')) {
  class CartBundleValidation extends HTMLElement {
    constructor() {
      super();
      this.itemsToRemove = this.getAttribute('data-items-to-remove')?.split(',').filter(Boolean) || [];
      this.hasBundleParents = this.hasAttribute('data-has-bundle-parents');
    }

    connectedCallback () {
      this.validateBundleItems();
    }

    async validateBundleItems() {
      // Check if we have any bundle items that need to be removed
      if (this.itemsToRemove.length) {
        let updates = Object.fromEntries(this.itemsToRemove?.map((item) => {
          return [
            item, 0
          ]
        }));

        try {
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ updates }),
          });

          if (!response.ok) {
            throw new Error(`Response status: ${response.status}`);
          }

          if (window.location.href.indexOf('/cart') >= 0) {
            window.location.reload();
          } else {
            document.dispatchEvent(new Event('cart:refresh'));
          }
        } catch (error) {
          console.error(error.message);
        }
      }

      // Check if we have any bundle parents missing items
      if (this.hasBundleParents) {
        const cart = await getCart();
        const bundleParents = cart.items.filter((item) => item.properties['_bundle_parent']);
        const freeGiftsInCart = cart.items.filter((item) => item.properties['_bundle_child']);

        for await (const bundleParent of bundleParents) {
          const bundleChildren = bundleParent.properties['_bundle_children'];
          const bundleId = bundleParent.properties['_bundle_id'];
          const bundleParentQuantity = bundleParent.quantity;
          const formData = {
            items: []
          };

          Array.from(bundleChildren).map((bundleChild) => {
            const freeGiftVariantId = bundleChild;
            const giftIsAlreadyInCart = freeGiftsInCart.find((freeGift) => freeGift.variant_id === parseInt(freeGiftVariantId));

            if (giftIsAlreadyInCart) { return; }

            formData.items.push({
              id: freeGiftVariantId,
              quantity: 1,
              properties: {
                _bundle_id: bundleId,
                _bundle_child: true,
              }
            });

            return null;
          });

          if (formData.items.length) {
            try {
              const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  id: bundleParent.key,
                  quantity: 0
                }),
              });

              if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
              }
            } catch (error) {
              console.error(error.message);
            }

            formData.items.push({
              id: bundleParent.variant_id,
              quantity: bundleParentQuantity,
              selling_plan: bundleParent.selling_plan_allocation?.selling_plan?.id,
              properties: {
                _bundle_id: bundleId,
                _bundle_parent: true,
                _program_name: bundleParent.properties['_program_name'],
                _bundle_children: bundleChildren
              }
            });

            try {
              const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData),
              });

              if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
              }

              document.dispatchEvent(new Event('cart:refresh'));
            } catch (error) {
              console.error(error.message);
            }
          }
        }
      }
    }
  }

  window.customElements.define('cart-bundle-validation', CartBundleValidation);
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.location.href.indexOf('?cart-redirect') === -1) { return; }

  const cartDrawerToggle = document.querySelector('a[aria-controls="cart-drawer"]');

  if (cartDrawerToggle) {
    cartDrawerToggle.click();
  }
});

/* Before after tabs */
document.addEventListener('DOMContentLoaded', () => {
  const beforeAfterTabs = document.querySelectorAll('[data-before-after-tab]');

  beforeAfterTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const beforeAfterWrapper = tab.closest('[data-before-after-wrapper]')
      const siblingTabs = beforeAfterWrapper.querySelectorAll('[data-before-after-tab]');
      const beforeAfterBlocks = beforeAfterWrapper.querySelectorAll('before-after');

      siblingTabs.forEach((siblingTab) => siblingTab.classList.remove('active'));
      tab.classList.add('active');

      beforeAfterBlocks.forEach((block) => {
        if (block.getAttribute('data-tab-name') == tab.getAttribute('data-tab-name')) {
          block.classList.remove('hidden');
        } else {
          block.classList.add('hidden');
        }
      });
    });
  });
});

async function getCart() {
  try {
    const response = await fetch('/cart.js', {
      method: 'GET',
      cache: 'no-cache'
    });

    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error(error.message);
  }
}